/**
 * Типы для продуктов и их компонентов (UI-ориентированная модель сущности).
 * Перенесено из ui/types/ — типы принадлежат model/, не ui/.
 */
import type { ProductBundle, BundleStepProductUnavailableReason } from './bundle'
import type { ComboGroupLayout } from '~/utils/types/ComboLayout';

export interface ProductImage {
  url: string;
  key?: string;
}

export interface ProductKBJU {
  proteins?: number;
  fats?: number;
  carbohydrates?: number;
  calories?: number;
}

export interface ProductAddon {
  id: number;
  name: string;
  price: number;
  img?: ProductImage;
}

export interface ProductExclude {
  id: number;
  name: string;
  img?: ProductImage;
}

export interface ProductVariant {
  id: number;
  name: string;
  size?: string;
  price: number;
  description?: string;
  amount?: number;
  volume?: number;
  weight?: number;
  stop_list?: boolean;
  tag?: string;
  teg?: string; // legacy поддержка
  kbju?: ProductKBJU;
  /** Состав блюда — обязателен для продуктов питания (п. 2 ст. 10 ЗоЗПП). */
  composition?: string | null;
  /** Аллергены — в карточке выводятся отдельным блоком. */
  allergens?: string | null;
  img?: ProductImage[];
  background_img?: ProductImage | null;
  addons?: ProductAddon[];
  excludes?: ProductExclude[];
  min_quantity?: number;
  quantity_step?: number;
  show_bundles?: boolean;
  bundles?: ProductBundle[];
}

export interface ProductGroup {
  type: 'group';
  id: number;
  name: string;
  img?: ProductImage[];
  variants: ProductVariant[];
  is_radio_button?: boolean;
}

export interface SingleProduct extends ProductVariant {
  type?: 'single';
  show_bundles?: boolean;
  bundles?: ProductBundle[];
}

// ============================================
// КОМБО ТОВАРЫ
// ============================================

export interface ComboGroupItem {
  id: number;
  /**
   * Цена ПОРЦИИ, входящей в комбо. У весового товара витрина показывает цену за
   * килограмм, а в комбо входит 250 г — бэк отдаёт уже пересчитанную цену, чтобы
   * гостю не приходилось считать долю в уме.
   */
  price: number;
  name: string;
  /** Цена за единицу из карточки товара — «1390 ₽/кг» */
  unit_price?: number;
  /** Сколько единиц входит в комбо: 0.25 (кг) или 2 (шт) */
  portion_quantity?: number;
  portion_unit?: 'kg' | 'pcs' | null;
  /** Готовая подпись порции: «250 г»; null — порция не задана */
  portion_label?: string | null;
  img: ProductImage[];
  size?: string | null;
  /** Состав блюда — показывается в карточке слота конструктора */
  description?: string | null;
  /** Граммовка — рядом с размером в карточке слота */
  weight?: number | null;
  is_default: boolean;
  sort_order: number;
  /**
   * false — товар показывается в конструкторе, но выбрать его нельзя. Поля добавлены
   * позже: у старых ответов их нет, и товар считается доступным.
   */
  is_available?: boolean;
  unavailable_reason?: BundleStepProductUnavailableReason | null;
}

export interface ComboGroup {
  id: number;
  name: string;
  description?: string;
  sort_order: number;
  is_required: boolean;
  apply_discount?: boolean;
  min_selections: number;
  max_selections: number;
  /** Место слота на составной картинке комбо; null — слот не расставлен */
  layout?: ComboGroupLayout | null;
  products: ComboGroupItem[];
}

export interface ComboProduct {
  type: 'combo';
  id: number;
  name: string;
  description?: string;
  discount_percent: number;
  show_discount_badge: boolean;
  base_price: number;
  discount_amount: number;
  final_price: number;
  img: ProductImage[];
  stock: number;
  sort_order?: number;
  tags?: number[];
  groups: ComboGroup[];
}

/** Выбранные позиции в группах комбо: { groupId: productId } */
export type ComboSelections = Record<number, number>;

/** Кастомизация слота комбо: добавки и убранные ингредиенты выбранного товара */
export interface ComboSlotCustomization {
  addons: number[];
  excludes: number[];
}

/** Кастомизация по группам: { groupId: { addons, excludes } } */
export type ComboCustomizations = Record<number, ComboSlotCustomization>;

export type Product = ProductGroup | SingleProduct | ComboProduct;

// ============================================
// TYPE GUARDS
// ============================================

export function isProductGroup(product: Product | null | undefined): product is ProductGroup {
  return product?.type === 'group' && 'variants' in product;
}

export function isSingleProduct(product: Product | null | undefined): product is SingleProduct {
  return !product || product.type !== 'group';
}

export function isComboProduct(product: Product | null | undefined): product is ComboProduct {
  return product?.type === 'combo' && 'groups' in product;
}
